import React, { useState } from "react";
import { CheckCircle2, ArrowRight } from "lucide-react";
import api from "@/lib/api";

const APPLIANCES = ["Refrigerator", "Washer", "Dryer", "Oven / Stove", "Dishwasher", "Other"];
const TIMES = ["8:00 AM - 10:00 AM", "10:00 AM - 12:00 PM", "12:00 PM - 2:00 PM", "2:00 PM - 4:00 PM", "4:00 PM - 6:00 PM"];
const CITIES = ["Bronx, NY", "Yonkers, NY", "Mount Vernon, NY", "New Rochelle, NY", "White Plains, NY", "Scarsdale, NY", "Greenwich, CT", "Stamford, CT", "Norwalk, CT", "Danbury, CT", "Other"];

const today = () => new Date().toISOString().slice(0, 10);

export default function Book() {
  const [form, setForm] = useState({
    customer_name: "", email: "", phone: "",
    address: "", city: "Bronx, NY",
    appliance: "Refrigerator", brand: "", problem: "",
    preferred_date: today(), preferred_time: TIMES[1],
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [err, setErr] = useState("");

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setSubmitting(true);
    try {
      const { data } = await api.post("/appointments", form);
      setDone(data);
    } catch (e) {
      const d = e?.response?.data?.detail;
      setErr(typeof d === "string" ? d : "Could not submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center">
        <CheckCircle2 className="w-16 h-16 text-brand mx-auto" />
        <h1 className="mt-6 font-display font-black text-5xl tracking-tighter">Request received.</h1>
        <p className="mt-4 text-[#475569]">Thanks, {done.customer_name}. We've been notified and will confirm your window shortly.</p>
        <div className="mt-10 border border-[#E2E8F0] p-6 text-left inline-block">
          <div className="text-mono-label">Reference</div>
          <div className="font-mono text-lg mt-2">{done.id.slice(0, 8).toUpperCase()}</div>
          <div className="divider-slash my-4" />
          <div className="text-sm"><span className="text-[#475569]">Appliance:</span> {done.appliance}</div>
          <div className="text-sm"><span className="text-[#475569]">When:</span> {done.preferred_date} — {done.preferred_time}</div>
        </div>
        <div className="mt-10">
          <a href="/" className="btn-outline">Back home</a>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-6 md:px-10 py-16">
      <div className="text-mono-label">Booking</div>
      <h1 className="font-display font-black text-5xl md:text-6xl mt-4 tracking-tighter">Book a repair.</h1>
      <p className="mt-4 text-[#475569] max-w-2xl">Fill this in — takes 60 seconds. You'll get a text back to confirm the window.</p>

      <form onSubmit={submit} className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-5">
        <Field label="Full name *"><input required data-testid="book-name" className="field" value={form.customer_name} onChange={(e) => update("customer_name", e.target.value)} /></Field>
        <Field label="Phone *"><input required data-testid="book-phone" className="field" value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="(347) 555-1234" /></Field>
        <Field label="Email *"><input required data-testid="book-email" type="email" className="field" value={form.email} onChange={(e) => update("email", e.target.value)} /></Field>
        <Field label="City / Area *">
          <select data-testid="book-city" className="field" value={form.city} onChange={(e) => update("city", e.target.value)}>
            {CITIES.map((c) => <option key={c}>{c}</option>)}
          </select>
        </Field>
        <Field label="Address *" full><input required data-testid="book-address" className="field" value={form.address} onChange={(e) => update("address", e.target.value)} placeholder="123 Main St, Apt 4" /></Field>

        <Field label="Appliance *">
          <select data-testid="book-appliance" className="field" value={form.appliance} onChange={(e) => update("appliance", e.target.value)}>
            {APPLIANCES.map((c) => <option key={c}>{c}</option>)}
          </select>
        </Field>
        <Field label="Brand"><input data-testid="book-brand" className="field" value={form.brand} onChange={(e) => update("brand", e.target.value)} placeholder="e.g. Samsung, LG, Bosch" /></Field>

        <Field label="Preferred date *"><input required data-testid="book-date" type="date" min={today()} className="field" value={form.preferred_date} onChange={(e) => update("preferred_date", e.target.value)} /></Field>
        <Field label="Window *">
          <select data-testid="book-time" className="field" value={form.preferred_time} onChange={(e) => update("preferred_time", e.target.value)}>
            {TIMES.map((t) => <option key={t}>{t}</option>)}
          </select>
        </Field>

        <Field label="Describe the problem *" full>
          <textarea required data-testid="book-problem" className="field min-h-[130px]" value={form.problem} onChange={(e) => update("problem", e.target.value)} placeholder="What's not working? Any error codes? How long has it been happening?" />
        </Field>

        {err && <div data-testid="book-error" className="md:col-span-2 text-sm text-red-400 border border-red-900 px-4 py-3">{err}</div>}

        <div className="md:col-span-2 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-4 border-t border-[#E2E8F0]">
          <p className="text-xs text-[#94A3B8] font-mono">SUBMIT · WE'LL TEXT YOU BACK · CONFIRMED WITHIN 24H</p>
          <button data-testid="book-submit" disabled={submitting} className="btn-brand">
            {submitting ? "Sending…" : "Request appointment"} <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children, full }) {
  return (
    <label className={`block ${full ? "md:col-span-2" : ""}`}>
      <div className="text-mono-label text-[#475569]">{label}</div>
      <div className="mt-2">{children}</div>
    </label>
  );
}
